import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Printer, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Order Confirmation Ticket — shown right after a successful checkout.
 *
 * Props:
 *   order            : the order object returned by the API (must contain
 *                      order_number, total, items[], created_at)
 *   savings          : total client savings vs PVP tarifa (float, EUR)
 *   listTotal        : PVP tarifa total (float, EUR)
 *   deliveryDate     : global delivery date if the user set one (string, ISO)
 *   onContinueShopping: called when the user hits "Volver al catálogo"
 */
const OrderSuccessModal = ({ order, savings, listTotal, deliveryDate, onContinueShopping }) => {
  const [reorderQtys, setReorderQtys] = useState({});
  if (!order) return null;

  const totalUnits = (order.items || []).reduce((sum, i) => sum + (i.quantity || 0), 0);
  const subtotalBase = order.total || 0;
  const IVA_RATE = 0.21;
  const iva = subtotalBase * IVA_RATE;
  const totalIva = subtotalBase * (1 + IVA_RATE);

  // Aggregate per-line delivery dates
  const dates = new Set();
  (order.items || []).forEach((it) => {
    if (it.delivery_date) dates.add(it.delivery_date);
  });
  const uniqueDates = Array.from(dates).sort();

  const createdAt = order.created_at
    ? new Date(order.created_at).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="order-success-modal"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Ticket top strip */}
        <div className="bg-black text-white px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            <p className="text-xs uppercase tracking-widest text-neutral-300">Pedido confirmado</p>
          </div>
          <h2 className="text-2xl font-black" data-testid="order-success-number">
            {order.order_number}
          </h2>
          <p className="text-xs text-neutral-400 mt-1">{createdAt}</p>
        </div>

        {/* Zig-zag divider */}
        <div className="h-3 bg-white relative">
          <div
            className="absolute inset-x-0 top-0 h-3 bg-black"
            style={{
              WebkitMask:
                'radial-gradient(circle at 8px 0, transparent 4px, black 4.5px) repeat-x',
              mask: 'radial-gradient(circle at 8px 0, transparent 4px, black 4.5px) repeat-x',
              maskSize: '16px 6px',
              WebkitMaskSize: '16px 6px',
            }}
          />
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Savings highlight */}
          {savings > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <p className="text-xs uppercase tracking-widest text-emerald-700">
                Ahorro con tu descuento −24%
              </p>
              <p className="text-3xl font-black text-emerald-700 mt-1" data-testid="order-success-savings">
                €{savings.toFixed(2)}
              </p>
              <p className="text-[11px] text-emerald-700 mt-1">
                (PVP Tarifa €{(listTotal || 0).toFixed(2)} → precio final €{subtotalBase.toFixed(2)})
              </p>
            </div>
          )}

          {/* Line summary */}
          <div className="border border-neutral-200 rounded-xl divide-y">
            <Row label="Referencias" value={(order.items || []).length} />
            <Row label="Unidades" value={totalUnits} />
            <Row label="Subtotal (base)" value={`€${subtotalBase.toFixed(2)}`} />
            <Row label="IVA 21%" value={`€${iva.toFixed(2)}`} />
            <Row label="Total IVA incl." value={`€${totalIva.toFixed(2)}`} highlight />
          </div>

          {/* Delivery date */}
          <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4">
            <p className="text-xs uppercase tracking-widest text-neutral-500 mb-1">
              📅 Fecha de entrega solicitada
            </p>
            {uniqueDates.length === 0 && !deliveryDate && (
              <p className="text-sm text-neutral-600">Sin fecha específica solicitada</p>
            )}
            {uniqueDates.length === 1 && (
              <p className="text-lg font-bold text-black">{formatDate(uniqueDates[0])}</p>
            )}
            {uniqueDates.length > 1 && (
              <div className="text-sm text-neutral-700 space-y-0.5">
                <p className="text-xs uppercase tracking-widest text-neutral-500">Por línea</p>
                {(order.items || []).map((it, i) =>
                  it.delivery_date ? (
                    <p key={i}>
                      · {it.model_name} · {it.color} · T-{it.size} → {formatDate(it.delivery_date)}
                    </p>
                  ) : null,
                )}
              </div>
            )}
            {uniqueDates.length === 0 && deliveryDate && (
              <p className="text-lg font-bold text-black">{formatDate(deliveryDate)}</p>
            )}
          </div>

          <p className="text-[11px] text-neutral-500 text-center">
            Recibirás confirmación por email en <strong>deportestomasinfo@gmail.com</strong> junto
            con el resumen de pedido y el CSV de compra a Macron generado automáticamente.
          </p>
        </div>

        {/* Actions */}
        <div className="p-6 pt-0 flex flex-col sm:flex-row gap-2">
          <Button
            data-testid="order-success-accept"
            onClick={onContinueShopping}
            className="flex-1 bg-black hover:bg-neutral-800 text-white font-bold py-6 text-base"
          >
            Aceptar y volver al catálogo
          </Button>
          <Link to="/my-orders" className="flex-1">
            <Button data-testid="order-success-view-orders" variant="outline" className="w-full border-2 border-black text-black hover:bg-black hover:text-white font-bold py-6 text-base">
              Ver mis pedidos
            </Button>
          </Link>
          <Button
            data-testid="order-success-print"
            onClick={() => window.print()}
            variant="outline"
            className="border-2 border-neutral-300 text-neutral-700 hover:bg-neutral-100 py-6"
          >
            <Printer className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, highlight }) => (
  <div className={`flex items-center justify-between px-4 py-2 ${highlight ? 'bg-black text-white font-black text-lg' : 'text-neutral-700'}`}>
    <span className="text-sm">{label}</span>
    <span className={highlight ? '' : 'font-semibold'}>{value}</span>
  </div>
);

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch (e) {
    return iso;
  }
};

export default OrderSuccessModal;
